import React, { useEffect } from 'react';
import {
  XCircleIcon,
  PhotoIcon,
  TrashIcon,
  PlusIcon,
  CogIcon,
  MapPinIcon,
  CreditCardIcon,
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  PuzzlePieceIcon
} from '@heroicons/react/24/outline';
import QuestionnaireBuilder from './components/QuestionnaireBuilder';
import AddonInventoryManager from './components/AddonInventoryManager';
import ImageUploadCrop from '../common/ImageUploadCrop';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const EnhancedServiceForm = ({
  serviceFormData,
  setServiceFormData,
  editingService,
  handleEditService,
  handleCreateService,
  resetServiceForm,
  photoPreview,
  setPhotoPreview,
  showServiceSettings,
  setShowServiceSettings,
  activeSettingsTab,
  setActiveSettingsTab,
  isUploadingPhoto = false,
  isSubmittingService = false
}) => {

  console.log('🔍 EnhancedServiceForm rendered - editingService:', !!editingService);
  console.log('🔍 EnhancedServiceForm - editingService.id:', editingService?.id);
  console.log('🔍 EnhancedServiceForm - serviceFormData.enableSessions:', serviceFormData?.enableSessions);
  console.log('🔍 EnhancedServiceForm - serviceFormData.sessions:', serviceFormData?.sessions);
  console.log('🔍 EnhancedServiceForm - sessions array length:', serviceFormData?.sessions?.length);
  console.log('🔍 EnhancedServiceForm - will show advanced form?:', serviceFormData?.enableSessions);

  // Debug: Log first session details if exists
  if (serviceFormData?.sessions && serviceFormData.sessions.length > 0) {
    console.log('🔍 EnhancedServiceForm - First session details:', serviceFormData.sessions[0]);
  }

  const settingsTabs = [
    { id: 'rsvps', name: 'RSVPs', icon: ClipboardDocumentCheckIcon },
    { id: 'questionnaire', name: 'Questionnaire', icon: ClipboardDocumentCheckIcon },
    { id: 'privacy', name: 'Privacy', icon: ShieldCheckIcon },
    { id: 'payment', name: 'Payments', icon: CreditCardIcon },
    { id: 'discounts', name: 'Discounts', icon: CreditCardIcon },
    { id: 'addons', name: 'Add-ons', icon: PuzzlePieceIcon },
    { id: 'communication', name: 'Communication', icon: EnvelopeIcon }
  ];

  // Reset to first tab when settings modal opens
  useEffect(() => {
    if (showServiceSettings) {
      setActiveSettingsTab('rsvps');
    }
  }, [showServiceSettings, setActiveSettingsTab]);

  const { token } = useAuth();

  // Upload image to server and return URLs
  const uploadImageToServer = async (imageBlob, filename, serviceId) => {
    console.log('📤 [EnhancedServiceForm] uploadImageToServer starting with params:', {
      blobSize: imageBlob.size,
      blobType: imageBlob.type,
      filename,
      serviceId,
      hasToken: !!token
    });

    if (!token) {
      console.error('❌ [EnhancedServiceForm] No authentication token available');
      throw new Error('Authentication required');
    }

    if (!serviceId) {
      console.error('❌ [EnhancedServiceForm] No service ID provided');
      throw new Error('Service ID is required for photo upload');
    }

    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append('photo', imageBlob, filename);
    formData.append('serviceId', serviceId);

    console.log('📤 [EnhancedServiceForm] FormData created, making request to /api/uploads/service-photo');

    try {
      const response = await axios.post('/api/uploads/service-photo', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('📥 [EnhancedServiceForm] Response received:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });

      if (response.data.success) {
        console.log('✅ [EnhancedServiceForm] Upload successful:', {
          urls: response.data.urls,
          baseKey: response.data.baseKey
        });
        return {
          success: true,
          urls: response.data.urls,
          baseKey: response.data.baseKey
        };
      } else {
        console.error('❌ [EnhancedServiceForm] Server returned success=false:', response.data);
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('❌ [EnhancedServiceForm] Request failed:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      console.error('Image upload error:', error);
      throw new Error(error.response?.data?.error || 'Failed to upload image');
    }
  };

  const handleImageSelect = async (imageData) => {
    console.log('🎯 [EnhancedServiceForm] handleImageSelect called with imageData:', {
      hasImageData: !!imageData,
      hasDataUrl: !!imageData?.dataUrl,
      dataUrlLength: imageData?.dataUrl?.length || 0,
      hasFile: !!imageData?.file,
      fileName: imageData?.file?.name
    });

    if (!imageData) {
      console.log('🗑️ [EnhancedServiceForm] Clearing photo data');
      setPhotoPreview(null);
      setServiceFormData({ ...serviceFormData, photoUrls: null, photoBaseKey: null, photo: null });
      return;
    }

    // Set preview immediately
    setPhotoPreview(imageData.dataUrl);

    // Convert dataUrl to blob for upload
    const response = await fetch(imageData.dataUrl);
    const blob = await response.blob();

    // Generate filename
    const mimeType = imageData.dataUrl.split(',')[0].split(':')[1].split(';')[0];
    const extension = mimeType.split('/')[1];
    const filename = `service-photo-${Date.now()}.${extension}`;

    // If editing existing service, upload immediately with real service ID
    if (editingService?.id) {
      console.log('🚀 [EnhancedServiceForm] Uploading photo for existing service:', editingService.id);
      console.log('🚀 [EnhancedServiceForm] Service details:', { id: editingService.id, name: editingService.name });
      console.log('🚀 [EnhancedServiceForm] Upload params:', {
        blobSize: blob.size,
        filename,
        token: token ? 'PRESENT' : 'MISSING'
      });

      try {
        console.log('📤 [EnhancedServiceForm] Calling uploadImageToServer...');
        const uploadResult = await uploadImageToServer(blob, filename, editingService.id);
        console.log('📥 [EnhancedServiceForm] Upload result received:', uploadResult);

        if (uploadResult && uploadResult.success) {
          console.log('✅ [EnhancedServiceForm] Photo uploaded successfully for existing service:', uploadResult);
          console.log('✅ [EnhancedServiceForm] S3 URLs received:', uploadResult.urls);
          console.log('✅ [EnhancedServiceForm] Base key received:', uploadResult.baseKey);

          // Update form data with S3 URLs
          setServiceFormData({
            ...serviceFormData,
            photoUrls: uploadResult.urls,
            photoBaseKey: uploadResult.baseKey,
            photo: null // Clear any old base64 data
          });
          console.log('✅ [EnhancedServiceForm] Form updated with S3 data, base64 cleared');
        } else {
          console.error('❌ [EnhancedServiceForm] Photo upload failed (result not success):', uploadResult?.error || 'Unknown error');
          console.error('❌ [EnhancedServiceForm] Full upload result:', uploadResult);
          // Fall back to base64 storage
          console.log('⚠️ [EnhancedServiceForm] S3 upload failed - storing base64 as fallback');
          setServiceFormData({ ...serviceFormData, photoUrls: imageData.dataUrl, photoBaseKey: null, photo: null });
        }
      } catch (error) {
        console.error('❌ [EnhancedServiceForm] Photo upload error (exception):', error);
        console.error('❌ [EnhancedServiceForm] Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          stack: error.stack
        });
        // Fall back to base64 storage
        console.log('⚠️ [EnhancedServiceForm] S3 upload exception - storing base64 as fallback');
        setServiceFormData({ ...serviceFormData, photoUrls: imageData.dataUrl, photoBaseKey: null, photo: null });
      }
    } else {
      // For new services, store base64 temporarily (will be uploaded after service creation)
      console.log('📝 [EnhancedServiceForm] Storing base64 for new service - will upload after creation');
      setServiceFormData({ ...serviceFormData, photoUrls: imageData.dataUrl, photoBaseKey: null, photo: null });
    }
  };

  const handleLocationChange = (e) => {
    const location = e.target.value;
    setServiceFormData({ ...serviceFormData, location });
    
    // TODO: Implement address auto-suggestion with Google Places API
    // For now, we'll just store the text
  };

  return (
    <>
      {/* Service Form Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          {/* Background overlay */}
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={resetServiceForm}></div>
          
          {/* Modal panel */}
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingService ? 'Edit Service or Event' : 'Add New Service or Event'}
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowServiceSettings(true)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    title="Advanced Settings"
                  >
                    <CogIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={resetServiceForm}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

        
              <form onSubmit={(e) => {
                console.log('🔍 EnhancedServiceForm onSubmit triggered');
                console.log('🔍 Form onSubmit - editingService:', !!editingService);
                console.log('🔍 Form onSubmit - will call:', editingService ? 'handleEditService' : 'handleCreateService');
                if (editingService) {
                  handleEditService(e);
                } else {
                  handleCreateService(e);
                }
              }} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Photo Upload with Drag & Drop and Cropping */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <PhotoIcon className="h-4 w-4 inline mr-1" />
                      Service Photo
                      {isUploadingPhoto && <span className="text-purple-600 ml-2">(Processing...)</span>}
                    </label>
                    <ImageUploadCrop
                      onImageSelect={handleImageSelect}
                      initialImage={photoPreview}
                      aspectRatio={16/9} // Widescreen aspect ratio for service photos
                      maxFileSize={25 * 1024 * 1024} // 25MB to match original
                      allowedTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp']}
                      cropEnabled={true}
                      className="w-full"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service or Event Name *
                    </label>
                    <input
                      type="text"
                      value={serviceFormData.name || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., Equipment Maintenance, Consultation, Delivery, Birthday Party"
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={serviceFormData.description || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="3"
                      placeholder="Describe what this service or event includes..."
                    />
                  </div>

                  {/* Location Field with Auto-suggestion */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <MapPinIcon className="h-4 w-4 inline mr-1" />
                      Location
                    </label>
                    <input
                      type="text"
                      value={serviceFormData.location || ''}
                      onChange={handleLocationChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Enter an address"
                    />
                  </div>

                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rate *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={serviceFormData.rate || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only update if it's a valid number or empty string
                        if (value === '' || (!isNaN(value) && !isNaN(parseFloat(value)))) {
                          setServiceFormData({...serviceFormData, rate: value});
                        }
                      }}
                      onBlur={(e) => {
                        // Round to 2 decimal places on blur to avoid floating-point display issues
                        const value = e.target.value;
                        if (value && !isNaN(value)) {
                          const rounded = Math.round(parseFloat(value) * 100) / 100;
                          setServiceFormData({...serviceFormData, rate: rounded.toString()});
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rate Type *
                    </label>
                    <select
                      value={serviceFormData.rateType || 'PER_PERSON'}
                      onChange={(e) => setServiceFormData({...serviceFormData, rateType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    >
                      <option value="PER_PERSON">Per Person</option>
                      <option value="HOURLY">Hourly</option>
                      <option value="FIXED">Fixed Rate</option>
                    </select>
                  </div>

                  {/* Payment Options Toggle */}
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">Show Payment Options</div>
                          <div className="text-sm text-gray-500">Allow customers to see payment information</div>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(serviceFormData.showPaymentOptions)}
                          onChange={(e) => setServiceFormData({...serviceFormData, showPaymentOptions: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Dynamic Payment Options Display */}
                  {serviceFormData.showPaymentOptions && (
                    <div className="md:col-span-2">
                      <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-900">Payment Information Preview</h4>
                          <button
                            type="button"
                            onClick={() => setShowServiceSettings(true)}
                            className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                          >
                            Configure Payment Options
                          </button>
                        </div>
                        
                        {/* Payment Methods Display */}
                        {serviceFormData.paymentMethods && Object.keys(serviceFormData.paymentMethods).some(key => serviceFormData.paymentMethods[key]) && (
                          <div>
                            <div className="text-sm font-medium text-gray-700 mb-2">Available Payment Methods</div>
                            <div className="grid grid-cols-2 gap-2">
                              {serviceFormData.paymentMethods?.venmo && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                  Venmo: {serviceFormData.paymentMethods.venmo}
                                </div>
                              )}
                              {serviceFormData.paymentMethods?.cashApp && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                  Cash App: {serviceFormData.paymentMethods.cashApp}
                                </div>
                              )}
                              {serviceFormData.paymentMethods?.paypal && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                                  PayPal: {serviceFormData.paymentMethods.paypal}
                                </div>
                              )}
                              {serviceFormData.paymentMethods?.zelle && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                                  Zelle: {serviceFormData.paymentMethods.zelle}
                                </div>
                              )}
                              {serviceFormData.paymentMethods?.applePay && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <div className="w-2 h-2 bg-gray-800 rounded-full mr-2"></div>
                                  Apple Pay: {serviceFormData.paymentMethods.applePay}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Empty State */}
                        {(!serviceFormData.paymentMethods || !Object.keys(serviceFormData.paymentMethods).some(key => serviceFormData.paymentMethods[key])) && (
                          <div className="text-sm text-gray-500 italic">
                            Click "Configure Payment Options" above to add payment methods.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Time Slots Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-900">Available Time Slots</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {(serviceFormData.sessions || []).map((session, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-900">Session {index + 1}</h4>
                          {serviceFormData.sessions && serviceFormData.sessions.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newSessions = serviceFormData.sessions.filter((_, i) => i !== index);
                                setServiceFormData({...serviceFormData, sessions: newSessions});
                              }}
                              className="text-red-600 hover:text-red-800"
                              title="Remove session"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Session Name
                            </label>
                            <input
                              type="text"
                              value={session.sessionName || ''}
                              onChange={(e) => {
                                const newSessions = [...(serviceFormData.sessions || [])];
                                newSessions[index] = {...newSessions[index], sessionName: e.target.value};
                                setServiceFormData({...serviceFormData, sessions: newSessions});
                              }}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              placeholder="e.g., Morning Workshop"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Date
                            </label>
                            <input
                              type="date"
                              value={session.date || ''}
                              onChange={(e) => {
                                const newSessions = [...(serviceFormData.sessions || [])];
                                newSessions[index] = {...newSessions[index], date: e.target.value};
                                setServiceFormData({...serviceFormData, sessions: newSessions});
                              }}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Start Time
                            </label>
                            <input
                              type="time"
                              value={session.startTime || ''}
                              onChange={(e) => {
                                const newSessions = [...(serviceFormData.sessions || [])];
                                newSessions[index] = {...newSessions[index], startTime: e.target.value};
                                setServiceFormData({...serviceFormData, sessions: newSessions});
                              }}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Duration (hours)
                            </label>
                            <input
                              type="number"
                              min="0.25"
                              step="0.25"
                              value={session.duration || ''}
                              onChange={(e) => {
                                const newSessions = [...(serviceFormData.sessions || [])];
                                newSessions[index] = {...newSessions[index], duration: parseFloat(e.target.value) || 0};
                                setServiceFormData({...serviceFormData, sessions: newSessions});
                              }}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              placeholder="1"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Available Slots
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={session.slotsAvailable !== undefined ? session.slotsAvailable : ''}
                              onChange={(e) => {
                                const newSessions = [...(serviceFormData.sessions || [])];
                                const value = e.target.value;
                                const newSlotCount = value === '' ? 0 : parseInt(value, 10);
                                newSessions[index] = {...newSessions[index], slotsAvailable: isNaN(newSlotCount) ? 0 : newSlotCount};
                                setServiceFormData({...serviceFormData, sessions: newSessions});
                              }}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              placeholder="0"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Session Description
                          </label>
                          <textarea
                            value={session.sessionDescription || ''}
                            onChange={(e) => {
                              const newSessions = [...(serviceFormData.sessions || [])];
                              newSessions[index] = {...newSessions[index], sessionDescription: e.target.value};
                              setServiceFormData({...serviceFormData, sessions: newSessions});
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            rows="2"
                            placeholder="Detailed description for this specific session..."
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => {
                        const newSession = {
                          id: crypto.randomUUID(),
                          sessionName: '',
                          date: '',
                          startTime: '',
                          duration: 1,
                          slotsAvailable: 1,
                          sessionDescription: ''
                        };
                        const newSessions = [...(serviceFormData.sessions || []), newSession];
                        setServiceFormData({...serviceFormData, sessions: newSessions});
                      }}
                      className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-300 hover:text-purple-600 flex items-center justify-center"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add Session
                    </button>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="border-t pt-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Notes for Customers
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Add special instructions, requirements, or information that customers should know when booking this service.
                    </p>
                    <textarea
                      value={serviceFormData.notes || ''}
                      onChange={(e) => setServiceFormData({...serviceFormData, notes: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="3"
                      placeholder="e.g., Please bring valid ID, Payment required at time of service, 24-hour cancellation policy..."
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={resetServiceForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isUploadingPhoto || isSubmittingService}
                    className={`px-4 py-2 text-white rounded-lg inline-flex justify-center items-center ${
                      (isUploadingPhoto || isSubmittingService)
                        ? 'bg-purple-400 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {(isSubmittingService || isUploadingPhoto) && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    {isSubmittingService
                      ? (editingService ? 'Updating Service...' : 'Creating Service...')
                      : isUploadingPhoto
                        ? 'Uploading Photo...'
                        : (editingService ? 'Update Service' : 'Create Service')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Settings Modal */}
      {showServiceSettings && (
        <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowServiceSettings(false)}></div>
            
            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" style={{ position: 'relative', zIndex: 10000 }}>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Advanced Settings</h2>
                  <button
                    onClick={() => setShowServiceSettings(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Settings Tabs - Mobile Optimized with Horizontal Scroll */}
                <div className="border-b border-gray-200 mb-4 sm:mb-6">
                  <div className="overflow-x-auto -mx-4 sm:mx-0 scrollbar-hide">
                    <div className="flex px-4 sm:px-0 space-x-4 sm:space-x-8 min-w-max">
                      {settingsTabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveSettingsTab(tab.id)}
                            className={`flex items-center py-3 sm:py-2 px-3 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap touch-manipulation flex-shrink-0 ${
                              activeSettingsTab === tab.id
                                ? 'border-purple-500 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <Icon className="h-4 w-4 mr-1 sm:mr-2" />
                            <span>{tab.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="mt-6 min-h-[300px]">

                  {activeSettingsTab === 'rsvps' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">RSVP Settings</h3>
                        
                        <div className="space-y-4">
                          {/* Max Participants */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Maximum Participants per Booking
                            </label>
                            <div className="flex items-center space-x-3">
                              <input
                                type="number"
                                min="1"
                                max="100"
                                value={serviceFormData.rsvpSettings?.maxParticipants || 1}
                                onChange={(e) => {
                                  const maxParticipants = parseInt(e.target.value) || 1;
                                  setServiceFormData(prev => ({
                                    ...prev,
                                    rsvpSettings: {
                                      ...prev.rsvpSettings,
                                      maxParticipants
                                    }
                                  }));
                                }}
                                className="block w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              />
                              <span className="text-sm text-gray-500">participants maximum</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              This limits how many participants can be selected when booking this service via the public link.
                            </p>
                          </div>

                          {/* Collect Names Toggle */}
                          <div className="flex items-start">
                            <div className="flex items-center h-5">
                              <input
                                id="collectNames"
                                type="checkbox"
                                checked={Boolean(serviceFormData.rsvpSettings?.collectNames)}
                                onChange={(e) => {
                                  setServiceFormData(prev => ({
                                    ...prev,
                                    rsvpSettings: {
                                      ...prev.rsvpSettings,
                                      collectNames: e.target.checked
                                    }
                                  }));
                                }}
                                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                            </div>
                            <div className="ml-3 text-sm">
                              <label htmlFor="collectNames" className="font-medium text-gray-700">
                                Collect Names
                              </label>
                              <p className="text-gray-500">Require names for each additional participant when booking</p>
                            </div>
                          </div>

                          {/* Enable Public Bookings */}
                          <div className="flex items-start">
                            <div className="flex items-center h-5">
                              <input
                                id="enablePublicBookings"
                                type="checkbox"
                                checked={Boolean(serviceFormData.rsvpSettings?.enablePublicBookings !== false)}
                                onChange={(e) => {
                                  setServiceFormData(prev => ({
                                    ...prev,
                                    rsvpSettings: {
                                      ...prev.rsvpSettings,
                                      enablePublicBookings: e.target.checked
                                    }
                                  }));
                                }}
                                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                            </div>
                            <div className="ml-3 text-sm">
                              <label htmlFor="enablePublicBookings" className="font-medium text-gray-700">
                                Enable Public Bookings
                              </label>
                              <p className="text-gray-500">Allow people to book this service through the public booking link</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSettingsTab === 'questionnaire' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Questionnaire</h3>
                        <p className="text-sm text-gray-600 mb-6">Create custom questions to collect additional information from customers during booking.</p>
                        
                        <QuestionnaireBuilder
                          questionnaire={serviceFormData.questionnaire}
                          onQuestionnaireChange={(questionnaire) => setServiceFormData({
                            ...serviceFormData,
                            questionnaire: questionnaire
                          })}
                        />
                        
                        {/* Legacy options - keeping for backward compatibility */}
                        <div className="border-t pt-6 mt-6">
                          <h4 className="text-md font-medium text-gray-900 mb-4">Standard Fields</h4>
                          <p className="text-sm text-gray-600 mb-4">These standard fields are always available in addition to your custom questions.</p>
                          
                          {/* Allergies/Dietary Restrictions */}
                          <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h5 className="text-sm font-medium text-gray-900">Allergies & Dietary Restrictions</h5>
                                <p className="text-xs text-gray-500 mt-1">Ask customers about allergies or dietary restrictions during booking.</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={Boolean(serviceFormData.questionnaire?.collectAllergies)}
                                  onChange={(e) => setServiceFormData({
                                    ...serviceFormData,
                                    questionnaire: {
                                      ...serviceFormData.questionnaire,
                                      collectAllergies: e.target.checked
                                    }
                                  })}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            </div>
                          </div>
                          
                          {/* Instagram Handle */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h5 className="text-sm font-medium text-gray-900">Ask Customers for Instagram Handle</h5>
                                <p className="text-xs text-gray-500 mt-1">Collect customer's Instagram handle for social media engagement.</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={Boolean(serviceFormData.questionnaire?.collectInstagram)}
                                  onChange={(e) => setServiceFormData({
                                    ...serviceFormData,
                                    questionnaire: {
                                      ...serviceFormData.questionnaire,
                                      collectInstagram: e.target.checked
                                    }
                                  })}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSettingsTab === 'privacy' && (
                    <div className="text-center py-8">
                      <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Privacy Settings</h3>
                      <p className="mt-1 text-sm text-gray-500">Control visibility and access permissions.</p>
                      <p className="mt-2 text-xs text-gray-400">Coming soon...</p>
                    </div>
                  )}

                  {activeSettingsTab === 'payment' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Options</h3>
                        
                        {/* Payment Methods */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Methods</h4>
                          <p className="text-xs text-gray-500 mb-4">
                            Configure how customers can pay for this service. Leave blank to hide a payment method.
                          </p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Venmo */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Venmo
                              </label>
                              <input
                                type="text"
                                value={serviceFormData.paymentMethods?.venmo || ''}
                                onChange={(e) => setServiceFormData({
                                  ...serviceFormData, 
                                  paymentMethods: {
                                    ...serviceFormData.paymentMethods,
                                    venmo: e.target.value
                                  }
                                })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                                placeholder="@username or phone number"
                              />
                            </div>

                            {/* Cash App */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Cash App
                              </label>
                              <input
                                type="text"
                                value={serviceFormData.paymentMethods?.cashApp || ''}
                                onChange={(e) => setServiceFormData({
                                  ...serviceFormData, 
                                  paymentMethods: {
                                    ...serviceFormData.paymentMethods,
                                    cashApp: e.target.value
                                  }
                                })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                                placeholder="$cashtag"
                              />
                            </div>

                            {/* PayPal */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                PayPal
                              </label>
                              <input
                                type="text"
                                value={serviceFormData.paymentMethods?.paypal || ''}
                                onChange={(e) => setServiceFormData({
                                  ...serviceFormData, 
                                  paymentMethods: {
                                    ...serviceFormData.paymentMethods,
                                    paypal: e.target.value
                                  }
                                })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                                placeholder="email or PayPal.me link"
                              />
                            </div>

                            {/* Zelle */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Zelle
                              </label>
                              <input
                                type="text"
                                value={serviceFormData.paymentMethods?.zelle || ''}
                                onChange={(e) => setServiceFormData({
                                  ...serviceFormData,
                                  paymentMethods: {
                                    ...serviceFormData.paymentMethods,
                                    zelle: e.target.value
                                  }
                                })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                                placeholder="email or phone number"
                              />
                            </div>

                            {/* Apple Pay */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Apple Pay
                              </label>
                              <input
                                type="text"
                                value={serviceFormData.paymentMethods?.applePay || ''}
                                onChange={(e) => setServiceFormData({
                                  ...serviceFormData,
                                  paymentMethods: {
                                    ...serviceFormData.paymentMethods,
                                    applePay: e.target.value
                                  }
                                })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                                placeholder="phone or email"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Payment Note */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Add a note to guests</h4>
                          <p className="text-xs text-gray-500 mb-3">
                            This note will appear in the payment options section of the public booking page.
                          </p>
                          <textarea
                            value={serviceFormData.paymentNote || ''}
                            onChange={(e) => setServiceFormData({
                              ...serviceFormData, 
                              paymentNote: e.target.value
                            })}
                            rows="3"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
                            placeholder="e.g., Payment is due before the event starts, or special payment instructions..."
                          />
                        </div>

                        {/* Tiered Pricing Toggle */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                            <div className="flex items-center">
                              <CreditCardIcon className="h-5 w-5 text-blue-500 mr-3" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">Tiered Pricing</div>
                                <div className="text-sm text-gray-500">Set different rates for different date ranges</div>
                              </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={Boolean(serviceFormData.enableTieredPricing)}
                                onChange={(e) => setServiceFormData({
                                  ...serviceFormData, 
                                  enableTieredPricing: e.target.checked,
                                  pricingTiers: e.target.checked ? (serviceFormData.pricingTiers || []) : []
                                })}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>

                          {/* Pricing Tiers Configuration */}
                          {serviceFormData.enableTieredPricing && (
                            <div className="mt-4 space-y-4">
                              <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Base Rate (Default/Fallback)</h4>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Rate</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={serviceFormData.rate || ''}
                                      onChange={(e) => setServiceFormData({...serviceFormData, rate: e.target.value})}
                                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                                      disabled
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                                    <select
                                      value={serviceFormData.rateType || ''}
                                      onChange={(e) => setServiceFormData({...serviceFormData, rateType: e.target.value})}
                                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                                      disabled
                                    >
                                      <option value="HOURLY">Hourly</option>
                                      <option value="FIXED">Fixed Rate</option>
                                      <option value="PER_PERSON">Per Person</option>
                                      <option value="PER_ITEM">Per Item</option>
                                    </select>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">This rate is used when no pricing tier is active or as a fallback.</p>
                              </div>

                              {/* Pricing Tiers */}
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-medium text-gray-900">Pricing Tiers</h4>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newTier = {
                                        id: Date.now().toString(),
                                        name: '',
                                        rate: '',
                                        rateType: serviceFormData.rateType || 'HOURLY',
                                        validFrom: '',
                                        validTo: ''
                                      };
                                      setServiceFormData({
                                        ...serviceFormData,
                                        pricingTiers: [...(serviceFormData.pricingTiers || []), newTier]
                                      });
                                    }}
                                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    <PlusIcon className="h-3 w-3 mr-1" />
                                    Add Tier
                                  </button>
                                </div>

                                {(serviceFormData.pricingTiers || []).map((tier, index) => (
                                  <div key={tier.id || index} className="bg-white border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex-1">
                                        <input
                                          type="text"
                                          placeholder="Tier name (e.g., Early Bird, Peak Season)"
                                          value={tier.name || ''}
                                          onChange={(e) => {
                                            const updatedTiers = [...(serviceFormData.pricingTiers || [])];
                                            updatedTiers[index] = { ...tier, name: e.target.value };
                                            setServiceFormData({...serviceFormData, pricingTiers: updatedTiers});
                                          }}
                                          className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm font-medium"
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updatedTiers = (serviceFormData.pricingTiers || []).filter((_, i) => i !== index);
                                          setServiceFormData({...serviceFormData, pricingTiers: updatedTiers});
                                        }}
                                        className="ml-3 text-red-600 hover:text-red-800"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Rate</label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          placeholder="0.00"
                                          value={tier.rate || ''}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || (!isNaN(value) && !isNaN(parseFloat(value)))) {
                                              const updatedTiers = [...(serviceFormData.pricingTiers || [])];
                                              updatedTiers[index] = { ...tier, rate: value };
                                              setServiceFormData({...serviceFormData, pricingTiers: updatedTiers});
                                            }
                                          }}
                                          onBlur={(e) => {
                                            const value = e.target.value;
                                            if (value && !isNaN(value)) {
                                              const rounded = Math.round(parseFloat(value) * 100) / 100;
                                              const updatedTiers = [...(serviceFormData.pricingTiers || [])];
                                              updatedTiers[index] = { ...tier, rate: rounded.toString() };
                                              setServiceFormData({...serviceFormData, pricingTiers: updatedTiers});
                                            }
                                          }}
                                          className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                                        <select
                                          value={tier.rateType || 'HOURLY'}
                                          onChange={(e) => {
                                            const updatedTiers = [...(serviceFormData.pricingTiers || [])];
                                            updatedTiers[index] = { ...tier, rateType: e.target.value };
                                            setServiceFormData({...serviceFormData, pricingTiers: updatedTiers});
                                          }}
                                          className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        >
                                          <option value="HOURLY">Hourly</option>
                                          <option value="FIXED">Fixed Rate</option>
                                          <option value="PER_PERSON">Per Person</option>
                                          <option value="PER_ITEM">Per Item</option>
                                        </select>
                                      </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Valid From</label>
                                        <input
                                          type="date"
                                          value={tier.validFrom || ''}
                                          onChange={(e) => {
                                            const updatedTiers = [...(serviceFormData.pricingTiers || [])];
                                            updatedTiers[index] = { ...tier, validFrom: e.target.value };
                                            setServiceFormData({...serviceFormData, pricingTiers: updatedTiers});
                                          }}
                                          className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Valid To</label>
                                        <input
                                          type="date"
                                          value={tier.validTo || ''}
                                          onChange={(e) => {
                                            const updatedTiers = [...(serviceFormData.pricingTiers || [])];
                                            updatedTiers[index] = { ...tier, validTo: e.target.value };
                                            setServiceFormData({...serviceFormData, pricingTiers: updatedTiers});
                                          }}
                                          className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}

                                {(!serviceFormData.pricingTiers || serviceFormData.pricingTiers.length === 0) && (
                                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                    <CreditCardIcon className="mx-auto h-8 w-8 text-gray-400" />
                                    <p className="mt-2 text-sm text-gray-500">No pricing tiers configured</p>
                                    <p className="text-xs text-gray-400">Click "Add Tier" to create your first pricing tier</p>
                                  </div>
                                )}

                                {/* Add Tier Button */}
                                <div className="mt-4">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newTier = {
                                        id: Date.now(),
                                        name: '',
                                        rate: '',
                                        rateType: 'PER_PERSON',
                                        validFrom: '',
                                        validTo: ''
                                      };
                                      const updatedTiers = [...(serviceFormData.pricingTiers || []), newTier];
                                      setServiceFormData({...serviceFormData, pricingTiers: updatedTiers});
                                    }}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <PlusIcon className="w-4 h-4 mr-2" />
                                    Add Pricing Tier
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSettingsTab === 'discounts' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Bundle Discount</h3>
                        <p className="text-sm text-gray-600 mb-6">Set a discount percentage that applies when customers select all available sessions for this service.</p>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center mb-4">
                            <CreditCardIcon className="h-5 w-5 text-blue-500 mr-2" />
                            <h4 className="text-sm font-medium text-gray-900">Bundle Discount Settings</h4>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Bundle Discount Percentage
                              </label>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={serviceFormData.bundleDiscount || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                                      setServiceFormData({
                                        ...serviceFormData,
                                        bundleDiscount: value
                                      });
                                    }
                                  }}
                                  className="block w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                  placeholder="0"
                                />
                                <span className="text-sm text-gray-500">%</span>
                              </div>
                              <p className="mt-2 text-xs text-gray-500">
                                This discount will be applied to the service total (excluding add-ons) when customers select all available sessions. Leave blank or set to 0 to disable bundle discounts.
                              </p>
                            </div>

                            {/* Preview */}
                            {serviceFormData.bundleDiscount && parseFloat(serviceFormData.bundleDiscount) > 0 && (
                              <div className="bg-white border border-gray-200 rounded-md p-3 mt-4">
                                <h5 className="text-sm font-medium text-gray-900 mb-2">Discount Preview</h5>
                                <div className="text-sm text-gray-600 space-y-1">
                                  <div>• Applies when all sessions are selected</div>
                                  <div>• {serviceFormData.bundleDiscount}% off service pricing only</div>
                                  <div>• Add-ons are not discounted</div>
                                  <div>• Works with tiered pricing if enabled</div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h5 className="text-sm font-medium text-yellow-800">Bundle Discount Requirements</h5>
                              <div className="mt-2 text-sm text-yellow-700">
                                <ul className="list-disc list-inside space-y-1">
                                  <li>Only applies to services with multiple sessions/time slots</li>
                                  <li>Customers must select ALL available sessions to receive the discount</li>
                                  <li>Discount applies to service pricing only, not add-ons</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSettingsTab === 'addons' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Add-ons</h3>
                        
                        {/* Add-ons Toggle */}
                        <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg mb-6">
                          <div className="flex items-center">
                            <PuzzlePieceIcon className="h-5 w-5 text-purple-500 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">Enable Add-ons</div>
                              <div className="text-sm text-gray-500">Allow customers to select additional add-ons for this service</div>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={Boolean(serviceFormData.enableAddons)}
                              onChange={(e) => setServiceFormData({
                                ...serviceFormData, 
                                enableAddons: e.target.checked,
                                addons: e.target.checked ? (serviceFormData.addons || []) : []
                              })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        </div>

                        {/* Add-ons Configuration */}
                        {serviceFormData.enableAddons && (
                          <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                              <h4 className="text-sm font-medium text-gray-900 mb-3">Available Add-ons</h4>
                              <p className="text-xs text-gray-500 mb-4">
                                Configure additional services or items that customers can add to their booking. Per-item add-ons support inventory tracking.
                              </p>

                              <AddonInventoryManager
                                serviceId={editingService?.id}
                                addons={serviceFormData.addons || []}
                                onAddonsChange={(updatedAddons) => setServiceFormData({...serviceFormData, addons: updatedAddons})}
                                onError={(error) => {
                                  console.error('Addon error:', error);
                                  // TODO: Add proper error handling/toast notification
                                  alert(error);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeSettingsTab === 'communication' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 flex items-center">
                          <EnvelopeIcon className="h-5 w-5 mr-2" />
                          Communication Settings
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Configure contact information shown to customers on public booking pages.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Support Contact Email
                        </label>
                        <input
                          type="email"
                          value={serviceFormData.communicationEmail || ''}
                          onChange={(e) => setServiceFormData({ 
                            ...serviceFormData, 
                            communicationEmail: e.target.value 
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="support@yourcompany.com"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          This email will be displayed on the public booking confirmation page as a contact point for customers with questions.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-between pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowServiceSettings(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    Cancel
                  </button>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Just close the modal - settings are already applied to serviceFormData as user types
                        setShowServiceSettings(false);
                      }}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                    >
                      Apply Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnhancedServiceForm;